import { useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { DatePicker, Select } from 'antd';
import { PageHeader } from '../components/yeni-design/primitives';
import { PeriodTabs, type DashboardPeriod } from '../components/PeriodTabs';
import { donemHesapla, donemAraligiMetni, type Donem } from '../components/satis-analiz/donem';
import { OzetSerit } from '../components/satis-analiz/OzetSerit';
import { KanalBlok } from '../components/satis-analiz/KanalBlok';
import { SubeBlok } from '../components/satis-analiz/SubeBlok';
import { ZamanBlok } from '../components/satis-analiz/ZamanBlok';
import { KategoriBlok } from '../components/satis-analiz/KategoriBlok';
import { DikkatBlok } from '../components/satis-analiz/DikkatBlok';
import { useSubeler } from '../components/satis-analiz/useSubeler';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';

const { RangePicker } = DatePicker;

const VARSAYILAN_BLOKLAR = ['ozet', 'kanal', 'sube', 'zaman', 'kategori', 'dikkat'];

interface SatisAnalizProps {
  screen: PanelScreen;
  reports: ReportConfig[];
}

export function SatisAnaliz({ screen, reports }: SatisAnalizProps) {
  const [period, setPeriod] = useState<DashboardPeriod>('aylik');
  const [ozelAralik, setOzelAralik] = useState<[Dayjs, Dayjs] | null>(null);
  // null = TUM subeler. Secim yalniz bu ekranda yasar (URL'ye ya da
  // localStorage'a yazilmaz) - "anlik" bir bakis, kalici bir tercih degil.
  const [sube, setSube] = useState<string | null>(null);

  const donem: Donem = useMemo(() => {
    if (period === 'ozel' && ozelAralik) {
      const bas = ozelAralik[0].format('YYYY-MM-DD');
      const bit = ozelAralik[1].format('YYYY-MM-DD');
      return { bas, bit, tekGun: bas === bit };
    }
    return donemHesapla(period);
  }, [period, ozelAralik]);

  const subeler = useSubeler(reports, donem);

  // Secili sube donem degisince listeden dusebilir (ör. o pencerede hic
  // satis yok). Secimi SESSIZCE tutmak "ATAKENT" yazip bos ekran gostermek
  // olurdu; listede yoksa TUM subelere donulur.
  const seciliSube = sube !== null && subeler.includes(sube) ? sube : null;

  const baslik = screen.config?.baslik?.trim() || screen.title;
  const bloklar = screen.config?.bloklar ?? VARSAYILAN_BLOKLAR;

  // Task 5-9 bloklari `ortak`'i kullanacak.
  const ortak = { reports, donem, sube: seciliSube, etiketler: screen.config?.etiketler ?? {} };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={baslik}
        actions={
          // 🔴 CANLIDA OLCULDU (2026-08-21, 390px iPhone): serit 548,9px
          // genisligindeydi ve `main` `overflow-x-hidden` tasidigi icin
          // KESILIYORDU - yatay kaydirma da yok. Sube secicinin sag kenari
          // 492,7px, tarih araligi ise tumden ekran disindaydi: telefonda
          // secici ERISILEMEZ durumdaydi. `flex-wrap` ile alt satira iner.
          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="donem-secici"
          >
            <PeriodTabs value={period} onChange={setPeriod} />
            {/* Sube suzgeci: yalniz BIRDEN FAZLA sube varsa anlamli. Tek
                subeli firmada secenek kutusu gurultudur, hic basilmaz. */}
            {subeler.length > 1 && (
              <div data-testid="sube-secici">
                <Select
                  size="small"
                  style={{ minWidth: 140 }}
                  value={seciliSube ?? '__tum__'}
                  onChange={(v) => setSube(v === '__tum__' ? null : v)}
                  options={[
                    { value: '__tum__', label: 'Tüm şubeler' },
                    ...subeler.map((s) => ({ value: s, label: s })),
                  ]}
                />
              </div>
            )}
            {/* Secili donemin gercek araligi — yalniz "Ozel" degil HER
                sekmede gorunur; kullanici "Aylik" derken hangi tarihlere
                baktigini burada okur. */}
            <span className="text-[12px] text-ink-500 tnum" data-testid="donem-araligi">
              {donemAraligiMetni(donem)}
            </span>
            {period === 'ozel' && (
              // RangePicker `data-testid`'yi HEM baslangic HEM bitis input'una
              // yayar (iki eslesme -> getByTestId hata verir); testid'yi
              // sarmalayici tek div'e koy.
              <div data-testid="ozel-aralik">
                <RangePicker
                  value={ozelAralik}
                  onChange={(v) =>
                    setOzelAralik(v && v[0] && v[1] ? [v[0], v[1]] : null)
                  }
                  format="DD.MM.YYYY"
                  allowClear
                />
              </div>
            )}
          </div>
        }
      />
      {bloklar.map((blok) => {
        switch (blok) {
          // Task 5-9 bloklari buraya baglanir. Bilinmeyen anahtar SESSIZCE atlanir
          // ki ileride blok eklendiginde eski config'ler kirilmasin.
          case 'ozet':
            return <OzetSerit key={blok} reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
          case 'kanal':
            return <KanalBlok key={blok} reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
          case 'sube':
            return <SubeBlok key={blok} reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
          case 'zaman':
            return <ZamanBlok key={blok} reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
          case 'kategori':
            return <KategoriBlok key={blok} reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
          case 'dikkat':
            return <DikkatBlok key={blok} reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
